import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../api/supabase';
import { useAuth } from '../context/AuthContext';
import { ACCOUNTS } from '../api/accounts';
import { fetchSupplierPayableLines } from '../api/payables';

export default function Dashboard() {
  const navigate = useNavigate();
  const { isAgent, dashboardWidgets } = useAuth();
  useEffect(() => { if (isAgent) navigate('/agent', { replace: true }); }, [isAgent, navigate]);
  const [stats, setStats] = useState({ clients: 0 });
  const [monthStats, setMonthStats] = useState({ tickets: 0, visas: 0, packages: 0, ticketRevenue: 0, visaRevenue: 0, packageRevenue: 0 });
  const [widgetTotals, setWidgetTotals] = useState({ total_payable: 0, total_receivable: 0 });
  const [accountWidgets, setAccountWidgets] = useState([]); // [{ id, account_code, account_name, balance }]
  const [drillDown, setDrillDown] = useState(null); // 'tickets' | 'visas' | 'packages'
  const [drillData, setDrillData] = useState([]);
  const [drillLoading, setDrillLoading] = useState(false);
  const [recentBookings, setRecentBookings] = useState([]);
  const [pendingVisas, setPendingVisas] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);
  const monthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).toISOString().slice(0, 10);
  const monthLabel = new Date().toLocaleString('default', { month: 'long', year: 'numeric' });

  useEffect(() => {
    const load = async () => {
      const capEnd = monthEnd + 'T23:59:59';
      const [c, tk, vs, bk2, rb, pv] = await Promise.all([
        supabase.from('clients').select('id', { count: 'exact', head: true }).eq('is_active', true),
        // Counted/summed straight from tickets/visas/bookings by created_at (actual work
        // done this month), not from invoices by issued_date — those two dates diverge a
        // lot in practice: staff commonly enter a ticket/visa today but the invoice tied to
        // it gets dated to an earlier month (catching up on a backlog), so an invoice-dated
        // count silently misses most of what actually happened this month. Confirmed live:
        // 313 visas created this month company-wide, but only 120 had an invoice ALSO dated
        // this month — the other 184 were invoiced with an earlier issued_date. Same
        // created_at-based convention already used on the Staff Performance page.
        supabase.from('tickets').select('sell_price').neq('ticket_status', 'VOID').gte('created_at', monthStart).lt('created_at', capEnd).limit(5000),
        supabase.from('visas').select('sell_price').gte('created_at', monthStart).lt('created_at', capEnd).limit(5000),
        supabase.from('bookings').select('total_sell').gte('created_at', monthStart).lt('created_at', capEnd).limit(5000),
        supabase.from('bookings').select('booking_ref, clients(full_name), packages(package_name), total_sell, sell_currency:sell_currency_id(code), booking_status, created_at').order('created_at', { ascending: false }).limit(5),
        supabase.from('visas').select('visa_ref, clients(full_name), visa_types(name, country), visa_status').eq('visa_status', 'APPLIED').limit(5),
      ]);
      const sumOf = (rows, key) => (rows || []).reduce((s, r) => s + (parseFloat(r[key]) || 0), 0);
      setStats({ clients: c.count || 0 });
      setMonthStats({
        tickets: (tk.data || []).length, visas: (vs.data || []).length, packages: (bk2.data || []).length,
        ticketRevenue: sumOf(tk.data, 'sell_price'),
        visaRevenue: sumOf(vs.data, 'sell_price'),
        packageRevenue: sumOf(bk2.data, 'total_sell'),
      });
      setRecentBookings(rb.data || []);
      setPendingVisas(pv.data || []);
      setLoading(false);
    };
    load();
    window.addEventListener('focus', load);
    return () => window.removeEventListener('focus', load);
  }, []);

  useEffect(() => {
    const widgets = dashboardWidgets || [];
    const needsPayable = widgets.includes('total_payable');
    const needsReceivable = widgets.includes('total_receivable');
    const accountIds = widgets.filter(w => w.startsWith('account:')).map(w => w.slice('account:'.length));
    if (!needsPayable && !needsReceivable && accountIds.length === 0) { setAccountWidgets([]); return; }

    const fetchLines = async (ids) => {
      const PAGE = 1000;
      let all = [], from = 0;
      while (true) {
        const { data } = await supabase
          .from('journal_lines')
          .select('account_id, base_debit, base_credit, journal_entries!inner(status, is_reversed, reversal_of)')
          .in('account_id', ids)
          .eq('journal_entries.status', 'POSTED')
          .range(from, from + PAGE - 1);
        if (!data?.length) break;
        // A reversed entry and its reversal net to zero and represent no real activity —
        // exclude both, or a corrected mistake inflates the total even though the true
        // balance is unaffected.
        all = all.concat(data.filter(l => !l.journal_entries?.is_reversed && !l.journal_entries?.reversal_of));
        if (data.length < PAGE) break;
        from += PAGE;
      }
      return all;
    };
    const balanceOf = (lines, id) => lines.filter(l => l.account_id === id)
      .reduce((s, l) => s + parseFloat(l.base_credit || 0) - parseFloat(l.base_debit || 0), 0);

    const loadWidgetTotals = async () => {
      let payable = 0, receivable = 0;
      if (needsPayable) {
        // Same calculation as the Accounts Payable page's Net Payable.
        const lines = await fetchSupplierPayableLines();
        payable = lines.reduce((s, l) => s + parseFloat(l.base_credit || 0) - parseFloat(l.base_debit || 0), 0);
      }
      if (needsReceivable) {
        const lines = await fetchLines([ACCOUNTS.AR]);
        receivable = lines.reduce((s, l) => s + parseFloat(l.base_debit || 0) - parseFloat(l.base_credit || 0), 0);
      }
      setWidgetTotals({ total_payable: payable, total_receivable: receivable });

      if (accountIds.length) {
        const [{ data: accs }, lines] = await Promise.all([
          supabase.from('chart_of_accounts').select('id, account_code, account_name').in('id', accountIds),
          fetchLines(accountIds),
        ]);
        const withBalances = (accs || [])
          .map(a => ({ ...a, balance: balanceOf(lines, a.id) }))
          .sort((a, b) => a.account_code.localeCompare(b.account_code));
        setAccountWidgets(withBalances);
      } else {
        setAccountWidgets([]);
      }
    };
    loadWidgetTotals();
  }, [dashboardWidgets]);

  const openDrillDown = async (type) => {
    if (drillDown === type) { setDrillDown(null); return; }
    setDrillDown(type);
    setDrillLoading(true);
    setDrillData([]);
    // Drill from tickets/visas/bookings directly by created_at — matches the summary count
    // above, which is now sourced the same way (see the comment on monthStats).
    const capEnd = monthEnd + 'T23:59:59';
    let data;
    if (type === 'tickets') {
      const { data: d } = await supabase.from('tickets')
        .select('id, ticket_ref, airline, sell_price, sell_currency:sell_currency_id(code), payment_status, clients(full_name)')
        .neq('ticket_status', 'VOID').gte('created_at', monthStart).lt('created_at', capEnd)
        .order('created_at', { ascending: false }).limit(100);
      data = d;
    } else if (type === 'visas') {
      const { data: d } = await supabase.from('visas')
        .select('id, visa_ref, visa_types(name, country), sell_price, sell_currency:sell_currency_id(code), payment_status, clients(full_name)')
        .gte('created_at', monthStart).lt('created_at', capEnd)
        .order('created_at', { ascending: false }).limit(100);
      data = d;
    } else {
      const { data: d } = await supabase.from('bookings')
        .select('id, booking_ref, packages(package_name), total_sell, sell_currency:sell_currency_id(code), payment_status, clients(full_name)')
        .gte('created_at', monthStart).lt('created_at', capEnd)
        .order('created_at', { ascending: false }).limit(100);
      data = d;
    }
    setDrillData(data || []);
    setDrillLoading(false);
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  const statusColor = { PENDING: 'badge-yellow', CONFIRMED: 'badge-green', COMPLETED: 'badge-blue', CANCELLED: 'badge-red' };
  const payColor = (s) => s === 'PAID' ? '#16a34a' : '#dc2626';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Dashboard</div>
          <div className="page-subtitle">Overview of your travel company</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Link to="/sale-quotations/new" className="btn btn-secondary">+ Quotation</Link>
          <Link to="/quotations/new" className="btn btn-secondary">+ Pro Forma</Link>
          <Link to="/invoices/new" className="btn btn-primary">+ Invoice</Link>
          <Link to="/vouchers/receipt/new" className="btn btn-secondary">+ Receipt</Link>
        </div>
      </div>

      {(dashboardWidgets?.includes('total_payable') || dashboardWidgets?.includes('total_receivable') || accountWidgets.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
          {dashboardWidgets.includes('total_payable') && (
            <Link to="/finance/ap" className="stat-card" style={{ borderLeft: '3px solid #dc2626', textDecoration: 'none' }}>
              <div className="stat-label">Payable</div>
              <div className="stat-value" style={{ color: '#dc2626' }}>LKR {widgetTotals.total_payable.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </Link>
          )}
          {dashboardWidgets.includes('total_receivable') && (
            <Link to="/finance/ar" className="stat-card" style={{ borderLeft: '3px solid #2563eb', textDecoration: 'none' }}>
              <div className="stat-label">Receivable</div>
              <div className="stat-value" style={{ color: '#2563eb' }}>LKR {widgetTotals.total_receivable.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </Link>
          )}
          {accountWidgets.map(a => (
            <Link key={a.id} to={`/accounts/${a.id}/ledger`} className="stat-card" style={{ borderLeft: '3px solid #ea580c', textDecoration: 'none' }}>
              <div className="stat-label">{a.account_name}</div>
              <div className="stat-value" style={{ color: '#ea580c' }}>LKR {a.balance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</div>
            </Link>
          ))}
        </div>
      )}

      {/* This Month */}
      <div style={{ marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
          {monthLabel} — {stats.clients.toLocaleString()} Active Clients
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
          {[
            { key: 'tickets', label: 'Tickets Issued', count: monthStats.tickets, revenue: monthStats.ticketRevenue, color: '#2563eb', bg: '#eff6ff' },
            { key: 'visas', label: 'Visa Applications', count: monthStats.visas, revenue: monthStats.visaRevenue, color: '#7c3aed', bg: '#f5f3ff' },
            { key: 'packages', label: 'Package Bookings', count: monthStats.packages, revenue: monthStats.packageRevenue, color: '#0891b2', bg: '#ecfeff' },
          ].map(({ key, label, count, revenue, color, bg }) => (
            <div
              key={key}
              onClick={() => openDrillDown(key)}
              style={{
                background: drillDown === key ? bg : 'white',
                border: `1.5px solid ${drillDown === key ? color : 'var(--border)'}`,
                borderRadius: 10, padding: '16px 20px', cursor: 'pointer',
                transition: 'all 0.15s',
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 28, fontWeight: 700, color }}>{count}</div>
              <div style={{ fontSize: 12, color, marginTop: 4, fontWeight: 600 }}>
                LKR {revenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                {drillDown === key ? '▲ Hide details' : '▼ Click to expand'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Drill-down panel */}
      {drillDown && (
        <div className="card" style={{ marginTop: 0, borderTop: '3px solid var(--primary)', borderRadius: '0 0 10px 10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14 }}>
              {drillDown === 'tickets' ? 'Tickets This Month' : drillDown === 'visas' ? 'Visas This Month' : 'Package Bookings This Month'}
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => setDrillDown(null)}>✕ Close</button>
          </div>
          {drillLoading ? (
            <div style={{ padding: 20, textAlign: 'center' }}><div className="spinner" /></div>
          ) : drillData.length === 0 ? (
            <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No records this month.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--border)', color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase' }}>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Ref</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Client</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px' }}>Description</th>
                  <th style={{ textAlign: 'right', padding: '6px 8px' }}>Amount</th>
                  <th style={{ textAlign: 'center', padding: '6px 8px' }}>Payment</th>
                </tr>
              </thead>
              <tbody>
                {drillData.map((row) => {
                  const ref = row.ticket_ref || row.visa_ref || row.booking_ref;
                  const amount = parseFloat(row.sell_price ?? row.total_sell ?? 0);
                  const currCode = row.sell_currency?.code || '';
                  const desc = row.airline || (row.visa_types ? [row.visa_types.country, row.visa_types.name].filter(Boolean).join(' — ') : row.packages?.package_name) || '';
                  const routeTo = row.ticket_ref ? '/tickets' : row.visa_ref ? '/visas' : '/bookings';
                  return (
                    <tr
                      key={row.id}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                      onClick={() => navigate(`${routeTo}/${row.id}`)}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <td style={{ padding: '7px 8px', fontWeight: 600, color: 'var(--primary)' }}>{ref}</td>
                      <td style={{ padding: '7px 8px' }}>{row.clients?.full_name}</td>
                      <td style={{ padding: '7px 8px', color: 'var(--text-muted)' }}>{desc}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'right' }}>{currCode} {amount.toLocaleString()}</td>
                      <td style={{ padding: '7px 8px', textAlign: 'center', color: payColor(row.payment_status), fontWeight: 600, fontSize: 11 }}>{row.payment_status || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginTop: 4 }}>
        <div className="card">
          <div className="card-header"><div className="card-title">Recent Bookings</div><Link to="/bookings" className="btn btn-secondary btn-sm">View all</Link></div>
          {recentBookings.length === 0
            ? <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>No bookings yet</div>
            : recentBookings.map(bk => (
              <div key={bk.booking_ref} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <div>
                  <div style={{ fontWeight: 600 }}>{bk.clients?.full_name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: 12 }}>{bk.packages?.package_name || 'No package'} &mdash; {bk.booking_ref}</div>
                </div>
                <span className={`badge ${statusColor[bk.booking_status] || 'badge-gray'}`}>{bk.booking_status}</span>
              </div>
            ))
          }
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div className="card">
            <div className="card-header"><div className="card-title">Quick Actions</div></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { to: '/sale-quotations/new', label: '+ New Quotation' },
                { to: '/quotations/new', label: '+ New Pro Forma' },
                { to: '/invoices/new', label: '+ New Invoice' },
                { to: '/packages/new', label: '+ New Package' },
                { to: '/bills/new', label: '+ New Bill' },
                { to: '/vouchers/payment/new', label: '+ Payment Voucher' },
                { to: '/vouchers/receipt/new', label: '+ Receipt Voucher' },
              ].map(a => (
                <Link key={a.to} to={a.to} className="btn btn-secondary" style={{ justifyContent: 'center' }}>{a.label}</Link>
              ))}
            </div>
          </div>

          {pendingVisas.length > 0 && (
            <div className="card">
              <div className="card-header"><div className="card-title">Pending Visa Approvals</div><Link to="/visas" className="btn btn-secondary btn-sm">View all</Link></div>
              {pendingVisas.map(v => (
                <div key={v.visa_ref} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                  <span>{v.clients?.full_name}</span>
                  <span style={{ color: 'var(--text-muted)' }}>{v.visa_types?.country}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
